import React, { useState, useEffect, useMemo } from 'react';
import { DollarSign, Eye, Search, Wrench, User, Calendar } from 'lucide-react';
import { solicitudesService } from '../../../../services/api';
import OrdenTrabajoDetailModal from './OrdenTrabajoDetailModal';

const ManoObraTab = () => {
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrden, setSelectedOrden] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    cargarOrdenes();
  }, []);

  const cargarOrdenes = async () => {
    try {
      setLoading(true);
      const res = await solicitudesService.getAll({});
      const ordenesTrabajo = (res.data || []).filter(s => s.vehiculo_id != null);
      setOrdenes(ordenesTrabajo);
    } catch (error) {
      console.error('Error cargando órdenes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (orden) => {
    setSelectedOrden(orden);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedOrden(null);
    setShowModal(false);
  };

  const handleOrdenUpdated = () => {
    cargarOrdenes();
  };

  const totalMontoArticulos = useMemo(() =>
    ordenes.reduce((sum, o) => {
      const articulos = o.articulos || [];
      return sum + articulos.reduce((s, a) => s + (Number(a.costo) || 0) * (Number(a.cantidad) || 0), 0);
    }, 0),
    [ordenes]
  );

  const totalManoObra = useMemo(() =>
    ordenes.reduce((sum, o) => sum + (Number(o.mano_obra_total) || 0), 0),
    [ordenes]
  );

  const ordenesFiltradas = ordenes.filter(orden =>
    orden.nombre_solicitud?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    orden.usuario_creador_nombre?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-bold text-amber-800 flex items-center gap-2">
          <Wrench size={20} />
          Mano de Obra
        </h2>
        <p className="text-sm text-gray-600">
          Órdenes de trabajo con detalle de artículos, fechas y responsables
        </p>
      </div>

      <div className="p-4 bg-gray-50 border-b border-gray-200">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por orden o responsable..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-4 py-2 bg-blue-100 border border-blue-300 rounded-lg">
              <DollarSign size={16} className="text-blue-700" />
              <span className="text-sm font-semibold text-blue-800">
                Mano de Obra: ${totalManoObra.toFixed(2)}
              </span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-100 border border-amber-300 rounded-lg">
              <DollarSign size={16} className="text-amber-700" />
              <span className="text-sm font-semibold text-amber-800">
                Artículos: ${totalMontoArticulos.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-2 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : ordenesFiltradas.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Wrench size={40} className="mx-auto mb-2 text-gray-300" />
            <p className="font-medium">No hay órdenes de trabajo</p>
            <p className="text-sm mt-1">
              Las órdenes creadas desde Multi-Servicios aparecerán aquí
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100 border-b border-gray-200">
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Orden de Trabajo</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Responsable</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-600">Fecha</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600">Mano de Obra</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600">Costo Artículos</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-600">Acción</th>
                </tr>
              </thead>
              <tbody>
                {ordenesFiltradas.map((orden) => {
                  const montoArticulos = (orden.articulos || []).reduce(
                    (s, a) => s + (Number(a.costo) || 0) * (Number(a.cantidad) || 0), 0
                  );
                  const manoObra = Number(orden.mano_obra_total) || 0;
                  return (
                    <tr
                      key={orden.id}
                      className="border-b border-gray-100 hover:bg-amber-50/50 transition-colors cursor-pointer"
                      onClick={() => handleOpenModal(orden)}
                    >
                      <td className="px-4 py-3 text-gray-700">
                        <div className="font-medium text-gray-800">{orden.nombre_solicitud}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 text-gray-600">
                          <User size={14} className="text-gray-400" />
                          <span>{orden.usuario_creador_nombre || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1.5 text-gray-600">
                          <Calendar size={14} className="text-gray-400" />
                          <span>{orden.fecha_creacion ? new Date(orden.fecha_creacion).toLocaleDateString() : 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-blue-700">
                        ${manoObra.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-amber-700">
                        ${montoArticulos.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleOpenModal(orden); }}
                          className="p-2 text-amber-600 hover:bg-amber-100 rounded-lg transition-colors"
                          title="Ver detalle"
                        >
                          <Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 font-semibold border-t-2 border-gray-300">
                  <td colSpan="3" className="px-4 py-3 text-right text-gray-800">Totales</td>
                  <td className="px-4 py-3 text-right text-blue-900">${totalManoObra.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right text-amber-900">${totalMontoArticulos.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {showModal && selectedOrden && (
        <OrdenTrabajoDetailModal
          orden={selectedOrden}
          onClose={handleCloseModal}
          onUpdated={handleOrdenUpdated}
        />
      )}
    </div>
  );
};

export default ManoObraTab;
